import type { AnalyticsUserEvent } from '@fruitful/types';
import WebMixpanel from 'mixpanel-browser';

import { Singleton } from '../../common/utils/Singleton';
import { MIXPANEL_TOKEN } from '../../config/config';
import { IS_LOCAL } from '../../config/FruitfulEnvironment';
import type { User } from '../../user/types/User';
import {
    getMixpanelUserProperties,
    getMixpanelVisitorSuperProperties,
} from '../utils/UserTraits';
import { isMixpanelEnabled } from './MixpanelHelpers';
import type { IMixpanel } from './types/Mixpanel';

class MixpanelFacade extends Singleton implements IMixpanel {
    public readonly Mixpanel: typeof WebMixpanel;

    constructor() {
        super();
        this.Mixpanel = WebMixpanel;
    }

    public init(): void {
        if (!isMixpanelEnabled()) {
            return;
        }

        this.Mixpanel.init(MIXPANEL_TOKEN as string, {
            autocapture: false, // Disabling because default config generates tons of logs we don't need - https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#autocapture
            persistence: 'localStorage',
            ignore_dnt: true,
            debug: IS_LOCAL, // To use, you must also override logic within isMixpanelEnabled()
        });
    }

    public identifyUser(user: User): void {
        if (!isMixpanelEnabled()) {
            return;
        }

        this.Mixpanel.identify(user.id);
        this.Mixpanel.people.set(getMixpanelUserProperties(user));
        this.Mixpanel.register({ isVisitor: false });
    }

    public async getDistinctId(): Promise<string | undefined> {
        if (!isMixpanelEnabled()) {
            return undefined;
        }
        const id: unknown = await Promise.resolve(
            this.Mixpanel.get_distinct_id(),
        );
        return typeof id === 'string' && id !== '' ? id : undefined;
    }

    public identifyVisitor(visitorId: string): void {
        if (!isMixpanelEnabled()) {
            return;
        }

        // Mixpanel recommends not calling identify() for anonymous visitors; attach visitorId to events instead.
        this.Mixpanel.register(getMixpanelVisitorSuperProperties(visitorId));
    }

    public track(
        event: AnalyticsUserEvent,
        data?: Record<string, string | number | boolean>,
        sendImmediately?: boolean,
    ): void {
        if (!isMixpanelEnabled()) {
            return;
        }

        if (sendImmediately) {
            this.Mixpanel.track(event, data, {
                send_immediately: true,
            });
        } else {
            this.Mixpanel.track(event, data);
        }
    }

    public reset(): void {
        if (!isMixpanelEnabled()) {
            return;
        }

        this.Mixpanel.reset();
    }
}

export default MixpanelFacade.getInstance();
